<!-- 连连提现密码控件：token、userId、oidPartner 从链接读取，唤起 cashout_password -->
<template>
    <div class="cashout-pwd">
        <div class="card">
            <div class="badge">提现验证</div>
            <h1 class="title">请输入支付密码</h1>
            <p class="desc">用于验证提现身份，密码由连连安全控件加密后提交</p>

            <ul class="info-list" v-if="userId || oidPartner">
                <li class="info-item" v-if="userId">
                    <span class="label">用户号</span>
                    <span class="value">{{ maskedUserId }}</span>
                </li>
                <li class="info-item" v-if="oidPartner">
                    <span class="label">商户号</span>
                    <span class="value mono">{{ maskedOidPartner }}</span>
                </li>
            </ul>

            <p class="error" v-if="errorMsg">{{ errorMsg }}</p>
            <p class="hint" v-else-if="statusHint">{{ statusHint }}</p>

            <button
                type="button"
                class="action-btn"
                :disabled="!canInvoke || loading"
                @click="openPassword"
            >
                {{ loading ? '加载中…' : '输入支付密码' }}
            </button>
        </div>

        <!-- 密码回调成功：展示加密结果 -->
        <div v-if="showResultDialog" class="result-mask" @click.self="closeResultDialog">
            <div class="result-dialog">
                <h2 class="result-title">密码验证成功</h2>
                <p class="result-desc">以下为加密后的密码数据，请复制后提交给后端</p>

                <div class="result-field">
                    <div class="result-field__head">
                        <span class="result-field__label">password</span>
                        <button type="button" class="copy-btn" @click="copyText(resultPassword, 'password')">
                            {{ copiedField === 'password' ? '已复制' : '复制' }}
                        </button>
                    </div>
                    <div class="result-field__value">{{ resultPassword || '—' }}</div>
                </div>

                <div class="result-field">
                    <div class="result-field__head">
                        <span class="result-field__label">randomKey</span>
                        <button type="button" class="copy-btn" @click="copyText(resultRandomKey, 'randomKey')">
                            {{ copiedField === 'randomKey' ? '已复制' : '复制' }}
                        </button>
                    </div>
                    <div class="result-field__value">{{ resultRandomKey || '—' }}</div>
                </div>

                <button type="button" class="result-close" @click="closeResultDialog">我知道了</button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'

/** 连连环境：ste 测试 / ste2 新测试 / production 生产 / uat 预发（可被链接 env 覆盖） */
// const LLP_ENV = process.env.NODE_ENV === 'production' ? 'production' : 'ste'
const LLP_ENV = 'production'
const LLP_SDK_URL = 'https://oss-mpay-static.lianlianpay.com/tps/lib/accp-password/v2.4.2.js'

export default {
    setup() {
        const { proxy } = getCurrentInstance()
        const loading = ref(false)
        const errorMsg = ref('')
        const statusHint = ref('')
        const sdkReady = ref(false)
        const showResultDialog = ref(false)
        const resultPassword = ref('')
        const resultRandomKey = ref('')
        const copiedField = ref('')

        const readParam = (name) => {
            const aliases = Array.isArray(name) ? name : [name]
            for (const key of aliases) {
                const fromHash = proxy.getHashParam(key)
                if (fromHash != null && fromHash !== '') {
                    try {
                        return decodeURIComponent(fromHash)
                    } catch (e) {
                        return fromHash
                    }
                }
                const fromSearch = new URLSearchParams(window.location.search).get(key)
                if (fromSearch != null && fromSearch !== '') {
                    return fromSearch
                }
                const routeQuery = proxy.$route && proxy.$route.query
                    ? proxy.$route.query[key]
                    : null
                if (routeQuery != null && routeQuery !== '') {
                    return Array.isArray(routeQuery) ? routeQuery[0] : String(routeQuery)
                }
            }
            return ''
        }

        // token / userId / oidPartner 均从链接读取
        const passwordElementToken = ref(
            readParam(['passwordElementToken', 'token', 'password_element_token']) || ''
        )
        const userId = ref(readParam(['userId', 'user_id']) || '')
        const oidPartner = ref(readParam(['oidPartner', 'oid_partner']) || '')
        const passwordScene = ref(
            readParam(['passwordScene', 'password_scene']) || 'cashout_password'
        )
        const env = ref(readParam(['env']) || LLP_ENV)

        const canInvoke = computed(() => {
            return !!(passwordElementToken.value && userId.value && oidPartner.value && sdkReady.value)
        })

        const maskId = (raw) => {
            const id = String(raw || '')
            if (!id) return ''
            if (id.length <= 2) return '*'.repeat(id.length)
            if (id.length <= 4) return id[0] + '*'.repeat(id.length - 2) + id[id.length - 1]
            return id.slice(0, 2) + '*'.repeat(Math.min(id.length - 4, 4)) + id.slice(-2)
        }

        const maskedUserId = computed(() => maskId(userId.value))
        const maskedOidPartner = computed(() => maskId(oidPartner.value))

        const loadSdk = () => {
            return new Promise((resolve, reject) => {
                if (window.lianlianpay && typeof window.lianlianpay.invokePasswordControl === 'function') {
                    resolve()
                    return
                }
                const existed = document.querySelector(`script[src="${LLP_SDK_URL}"]`)
                if (existed) {
                    existed.addEventListener('load', () => resolve())
                    existed.addEventListener('error', () => reject(new Error('密码控件脚本加载失败')))
                    return
                }
                const script = document.createElement('script')
                script.src = LLP_SDK_URL
                script.async = true
                script.onload = () => resolve()
                script.onerror = () => reject(new Error('密码控件脚本加载失败'))
                document.head.appendChild(script)
            })
        }

        /** 从回调结果中提取 password / randomKey */
        const extractPasswordResult = (res) => {
            const data = res && res.data ? res.data : res || {}
            return {
                password: data.password || res.password || res.passwordHash || '',
                randomKey: data.randomKey || data.random_key || res.randomKey || res.random_key || '',
            }
        }

        const closeResultDialog = () => {
            showResultDialog.value = false
            copiedField.value = ''
        }

        const copyText = async (text, field) => {
            const value = String(text || '')
            if (!value) {
                proxy.$toast({ position: 'bottom', message: '暂无可复制内容' })
                return
            }
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(value)
                } else {
                    const input = document.createElement('textarea')
                    input.value = value
                    input.style.position = 'fixed'
                    input.style.opacity = '0'
                    document.body.appendChild(input)
                    input.select()
                    document.execCommand('copy')
                    document.body.removeChild(input)
                }
                copiedField.value = field
                proxy.$toast({ position: 'bottom', message: '复制成功' })
                setTimeout(() => {
                    if (copiedField.value === field) {
                        copiedField.value = ''
                    }
                }, 2000)
            } catch (err) {
                console.error('[cashoutPassword] copy failed', err)
                proxy.$toast({ position: 'bottom', message: '复制失败，请手动复制' })
            }
        }

        const postToApp = (payload) => {
            const message = JSON.stringify(payload)
            try {
                if (typeof cashoutPassword !== 'undefined' && cashoutPassword && typeof cashoutPassword.postMessage === 'function') {
                    cashoutPassword.postMessage(message)
                    return true
                }
                if (window.cashoutPassword && typeof window.cashoutPassword.postMessage === 'function') {
                    window.cashoutPassword.postMessage(message)
                    return true
                }
                if (
                    window.webkit &&
                    window.webkit.messageHandlers &&
                    window.webkit.messageHandlers.cashoutPassword
                ) {
                    window.webkit.messageHandlers.cashoutPassword.postMessage(message)
                    return true
                }
                if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
                    window.ReactNativeWebView.postMessage(message)
                    return true
                }
                console.warn('[cashoutPassword] WebView channel not found', message)
                return false
            } catch (err) {
                console.error('[cashoutPassword] postMessage failed', err)
                return false
            }
        }

        const showPasswordResult = (res) => {
            const { password, randomKey } = extractPasswordResult(res)
            resultPassword.value = password
            resultRandomKey.value = randomKey
            showResultDialog.value = true
            statusHint.value = '密码验证成功'

            const sent = postToApp({
                action: 'cashout_password',
                status: 'success',
                password,
                randomKey,
                data: res,
            })
            if (!sent) {
                statusHint.value = '密码已生成，请复制下方数据'
            }
        }

        const openPassword = () => {
            errorMsg.value = ''
            if (!passwordElementToken.value) {
                errorMsg.value = '缺少密码控件 Token，请检查链接参数'
                return
            }
            if (!userId.value) {
                errorMsg.value = '缺少用户号 userId，请检查链接参数'
                return
            }
            if (!oidPartner.value) {
                errorMsg.value = '缺少商户号 oidPartner，请检查链接参数'
                return
            }
            if (!window.lianlianpay || typeof window.lianlianpay.invokePasswordControl !== 'function') {
                errorMsg.value = '密码控件未就绪，请稍后重试'
                return
            }

            loading.value = true
            statusHint.value = '正在唤起密码键盘…'

            // 文档：https://oss-mpay-static.lianlianpay.com/tps/OpenPlatform/PasswordControl/password-sdk.html
            // passwordScene 从链接读取，缺省为 cashout_password（提现）
            window.lianlianpay.invokePasswordControl(
                {
                    passwordScene: passwordScene.value,
                    oidPartner: oidPartner.value,
                    userId: userId.value,
                    passwordElementToken: passwordElementToken.value,
                    env: env.value,
                    terminal: 'H5',
                    styles: {
                        themeColor: '#0f766e',
                    },
                },
                (res) => {
                    loading.value = false
                    console.log('[cashoutPassword] 密码控件回调', res)

                    // 用户取消或失败
                    if (!res || res.retCode === 'CANCEL' || res.ret_code === 'CANCEL') {
                        statusHint.value = '已取消输入'
                        postToApp({
                            action: 'cashout_password',
                            status: 'cancel',
                            data: res || null,
                        })
                        return
                    }

                    const failCode = res.retCode || res.ret_code
                    if (failCode && failCode !== '0000' && failCode !== 'SUCCESS') {
                        errorMsg.value = res.retMsg || res.ret_msg || '密码验证失败'
                        postToApp({
                            action: 'cashout_password',
                            status: 'fail',
                            data: res,
                        })
                        return
                    }

                    showPasswordResult(res)
                }
            )
        }

        onMounted(async () => {
            document.title = '提现密码'
            if (!passwordElementToken.value || !userId.value || !oidPartner.value) {
                const miss = []
                if (!passwordElementToken.value) miss.push('token')
                if (!userId.value) miss.push('userId')
                if (!oidPartner.value) miss.push('oidPartner')
                errorMsg.value = `链接缺少参数：${miss.join('、')}`
                return
            }
            try {
                loading.value = true
                statusHint.value = '正在加载安全控件…'
                await loadSdk()
                sdkReady.value = true
                statusHint.value = '控件已就绪，请点击下方按钮输入密码'
                // 自动唤起，减少一次点击（失败可手动再点）
                setTimeout(() => {
                    openPassword()
                }, 300)
            } catch (e) {
                errorMsg.value = e.message || '密码控件加载失败'
                sdkReady.value = false
            } finally {
                loading.value = false
            }
        })

        return {
            userId,
            oidPartner,
            maskedUserId,
            maskedOidPartner,
            canInvoke,
            loading,
            errorMsg,
            statusHint,
            showResultDialog,
            resultPassword,
            resultRandomKey,
            copiedField,
            openPassword,
            closeResultDialog,
            copyText,
        }
    },
}
</script>

<style scoped>
.cashout-pwd {
    --ink: #1c1917;
    --muted: #78716c;
    --line: #e7e5e4;
    --paper: #fffefb;
    --teal: #0f766e;
    --teal-dark: #115e59;
    --danger: #dc2626;
    min-height: 100vh;
    box-sizing: border-box;
    padding: 28px 16px 40px;
    background:
        radial-gradient(600px 280px at 12% -8%, rgba(15, 118, 110, 0.14), transparent 60%),
        linear-gradient(165deg, #fdf8f3 0%, #ecfdf5 48%, #f5f0e8 100%);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
    display: flex;
    justify-content: center;
}

.card {
    width: min(420px, 100%);
    background: var(--paper);
    border-radius: 6px 22px 6px 22px;
    border: 1px solid rgba(28, 25, 23, 0.08);
    box-shadow:
        10px 10px 0 rgba(15, 118, 110, 0.12),
        0 20px 40px rgba(28, 25, 23, 0.06);
    padding: 28px 22px 24px;
    box-sizing: border-box;
    text-align: center;
}

.badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--teal);
    background: rgba(15, 118, 110, 0.1);
    border: 1px solid rgba(15, 118, 110, 0.28);
    border-radius: 999px;
    padding: 4px 12px;
    margin-bottom: 14px;
}

.title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--ink);
}

.desc {
    margin: 10px 0 0;
    font-size: 13px;
    color: var(--muted);
    line-height: 1.55;
}

.info-list {
    list-style: none;
    margin: 20px 0 0;
    padding: 0;
    border-top: 1px dashed var(--line);
    text-align: left;
}

.info-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 2px;
    border-bottom: 1px dashed var(--line);
    font-size: 14px;
}

.info-item .label {
    color: var(--muted);
}

.info-item .value {
    color: var(--ink);
    font-weight: 500;
    word-break: break-all;
}

.info-item .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
}

.error {
    margin: 16px 0 0;
    color: var(--danger);
    font-size: 13px;
    line-height: 1.5;
}

.hint {
    margin: 16px 0 0;
    color: var(--muted);
    font-size: 13px;
    line-height: 1.5;
}

.action-btn {
    margin-top: 22px;
    width: 100%;
    height: 48px;
    border: none;
    border-radius: 2px 999px 2px 999px;
    background: var(--teal);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.08em;
    box-shadow: 0 4px 0 var(--teal-dark);
    cursor: pointer;
}

.action-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.action-btn:not(:disabled):active {
    transform: translateY(2px);
    box-shadow: 0 1px 0 var(--teal-dark);
}

.result-mask {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(28, 25, 23, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 16px;
    box-sizing: border-box;
}

.result-dialog {
    width: min(420px, 100%);
    max-height: 85vh;
    overflow-y: auto;
    background: var(--paper);
    border-radius: 12px 20px 12px 20px;
    padding: 22px 18px 18px;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18);
    text-align: left;
}

.result-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: var(--ink);
    text-align: center;
}

.result-desc {
    margin: 8px 0 18px;
    font-size: 13px;
    color: var(--muted);
    line-height: 1.5;
    text-align: center;
}

.result-field {
    margin-bottom: 14px;
    border: 1px dashed var(--line);
    border-radius: 8px;
    padding: 10px 12px;
    background: #f8fafc;
}

.result-field__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
}

.result-field__label {
    font-size: 13px;
    font-weight: 600;
    color: var(--teal);
}

.copy-btn {
    flex-shrink: 0;
    border: 1px solid rgba(15, 118, 110, 0.35);
    background: #fff;
    color: var(--teal);
    font-size: 12px;
    font-weight: 600;
    border-radius: 999px;
    padding: 4px 12px;
    cursor: pointer;
}

.result-field__value {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 1.55;
    color: var(--ink);
    word-break: break-all;
    max-height: 120px;
    overflow-y: auto;
}

.result-close {
    margin-top: 6px;
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 2px 999px 2px 999px;
    background: var(--teal);
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
}
</style>
